/**
 * ReferralBanner — bright, dismissible referral CTA for the landlord
 * dashboard. Shows once per session (per-user `sessionStorage` key)
 * so it doesn't nag every page load, and links to the existing
 * `/referrals` page where the reward flow already lives.
 *
 * Value prop: refer a landlord → both get 1 free month.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ArrowRight, X } from "lucide-react";

const DISMISS_KEY = "cr_referral_banner_dismissed_v1";

function isDismissed() {
  try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
}

export default function ReferralBanner() {
  const [dismissed, setDismissed] = useState(() => isDismissed());
  if (dismissed) return null;

  function close() {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* private mode */ }
    setDismissed(true);
  }

  return (
    <div
      data-testid="referral-banner"
      className="relative overflow-hidden rounded-2xl border border-emerald-300 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white p-4 md:p-5 shadow-lg shadow-emerald-600/20"
    >
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss referral banner"
        data-testid="referral-banner-dismiss"
        className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-white/10 text-white/80 hover:text-white transition-colors"
      >
        <X className="w-4 h-4"/>
      </button>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-none w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
          <Gift className="w-6 h-6 text-amber-200"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest font-bold text-emerald-100/90">
            Refer &amp; earn
          </div>
          <div className="mt-1 font-display text-lg md:text-xl font-extrabold leading-tight">
            Refer a landlord — you both get 1 month free.
          </div>
          <div className="mt-1 text-sm text-emerald-50/90">
            Share your link, they sign up, we credit both accounts on their first payment.
          </div>
        </div>
        <Link
          to="/referrals"
          data-testid="referral-banner-cta"
          className="flex-none inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-emerald-700 font-bold text-sm hover:bg-emerald-50 shadow-md transition-colors"
        >
          Get my link <ArrowRight className="w-4 h-4"/>
        </Link>
      </div>
    </div>
  );
}
